import { useAtomValue } from 'jotai';

import { activeWorkPanelProjectIdAtom, workPanelProjectIdAtomFamily } from './workPanel';

/**
 * Resolves which Work project a session's Work panel should render —
 * the Work-in-session analogue of ``useAutoSelectRecentSessionChat``.
 *
 * Read side only: writers are ``useCreateSessionWorkProject`` (new project)
 * and ``useSelectWorkPanelProject`` (switch to an existing one via
 * ``SessionWorkProjectSelector``). The "show this one now" override is
 * self-scoped (carries the ``sessionId`` it was set for), so this is a
 * pure derived read with no clear-on-change side effect to get wrong —
 * it can't leak into a different session no matter how ``SessionWorkPanel``
 * mounts/unmounts across session switches.
 */
/** Sentinel family key when no session is in scope — never written by create. */
const NO_SESSION_KEY = '__no_session__';

export const useWorkPanelProjectId = (sessionId: string | undefined): string | null => {
  const activeOverride = useAtomValue(activeWorkPanelProjectIdAtom);
  // Always subscribe (Rules of Hooks). Ignored when `sessionId` is missing so
  // an accidental write to `''` cannot surface another session's project.
  const recentProjectId = useAtomValue(
    workPanelProjectIdAtomFamily(sessionId ?? NO_SESSION_KEY)
  );

  if (!sessionId) return null;

  if (activeOverride && activeOverride.sessionId === sessionId) {
    return activeOverride.projectId;
  }

  return recentProjectId;
};
